import { HttpErrorResponse } from "@angular/common/http";
import { inject } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { withDevtools } from "@angular-architects/ngrx-toolkit";
import { patchState, signalStore, withMethods, withProps, withState } from "@ngrx/signals";
import { rxMethod } from "@ngrx/signals/rxjs-interop";
import { tapResponse } from "@ngrx/operators";
import { EMPTY, Subscription, catchError, firstValueFrom, pipe, switchMap, tap, timer } from "rxjs";
import {
  FREE_TIER_TOKEN_LIMIT,
  PENDING_SUBSCRIPTION_STORAGE_KEY,
  PENDING_PORTAL_VISIT_STORAGE_KEY,
  QUOTA_CRITICAL_THRESHOLD,
  QUOTA_WARNING_THRESHOLD,
  SNACKBAR_DURATION,
  SUBSCRIPTION_TOKEN_LIMIT,
} from "../constants";
import { StorageKeys } from "../enums";
import { QuotaExceededResponse, QuotaInfo, QuotaUsageStatus, QuotaViewModel, PaymentDialogReason } from "../interfaces";
import { withLoading } from "./features/loading.feature";
import {
  PaymentService,
  GenerateCheckoutDto,
  GeneratePortalDto,
  InlineResponse2001,
  InlineResponse2002,
  QuotaStatusDto,
  SubscriptionResponseDto,
} from "@api/bi-service";
import {
  SubscriptionRequiredDialogComponent,
  SubscriptionRequiredDialogData,
} from "../components/payment/subscription-required-dialog/subscription-required-dialog.component";
import { isPremiumSubscription } from "../utils/payment-subscription";

interface PaymentState {
  quota: QuotaStatusDto | null;
  subscription: SubscriptionResponseDto | null;
  quotaView: QuotaViewModel | null;
  initialized: boolean;
  quotaHydrated: boolean;
  subscriptionHydrated: boolean;
  quotaLoadStatus: PaymentLoadStatus;
  subscriptionLoadStatus: PaymentLoadStatus;
  premiumQuotaPending: boolean;
  isPollingSubscription: boolean;
  checkoutInFlight: boolean;
  portalInFlight: boolean;
  lastNotifiedStatus: QuotaUsageStatus | null;
  serviceUnavailable: boolean;
}

export type PaymentLoadStatus = "loading" | "ready" | "unavailable";

const isBrowser = (): boolean => typeof window !== "undefined" && typeof window.localStorage !== "undefined";

const markPendingCheckout = (): void => {
  if (isBrowser()) {
    window.localStorage.setItem(PENDING_SUBSCRIPTION_STORAGE_KEY, "true");
  }
};

const clearPendingCheckout = (): void => {
  if (isBrowser()) {
    window.localStorage.removeItem(PENDING_SUBSCRIPTION_STORAGE_KEY);
  }
};

const hasPendingCheckout = (): boolean => {
  if (!isBrowser()) return false;
  return window.localStorage.getItem(PENDING_SUBSCRIPTION_STORAGE_KEY) === "true";
};

const markPendingPortalVisit = (): void => {
  if (isBrowser()) {
    window.localStorage.setItem(PENDING_PORTAL_VISIT_STORAGE_KEY, "true");
  }
};

const clearPendingPortalVisit = (): void => {
  if (isBrowser()) {
    window.localStorage.removeItem(PENDING_PORTAL_VISIT_STORAGE_KEY);
  }
};

const hasPendingPortalVisit = (): boolean => {
  if (!isBrowser()) return false;
  return window.localStorage.getItem(PENDING_PORTAL_VISIT_STORAGE_KEY) === "true";
};

const computeNextResetDate = (): string => {
  const now = new Date();
  const nextMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));
  return nextMonth.toISOString();
};

const fallbackQuota = (): QuotaStatusDto => ({
  used: 0,
  limit: FREE_TIER_TOKEN_LIMIT,
  remaining: FREE_TIER_TOKEN_LIMIT,
  isExceeded: false,
  resetDate: computeNextResetDate(),
});

const normalizeNumber = (value: number | undefined, fallback = 0): number =>
  typeof value === "number" && !Number.isNaN(value) ? value : fallback;

const premiumEntitlementLimit = (subscription: SubscriptionResponseDto | null): number =>
  typeof subscription?.tokenQuota === "number" && subscription.tokenQuota > FREE_TIER_TOKEN_LIMIT
    ? subscription.tokenQuota
    : SUBSCRIPTION_TOKEN_LIMIT;

const isPremiumQuotaPending = (quota: QuotaStatusDto | null, subscription: SubscriptionResponseDto | null): boolean =>
  Boolean(quota && isPremiumSubscription(subscription) && quota.limit < premiumEntitlementLimit(subscription));

const mapQuotaView = (quota: QuotaStatusDto | null, subscription: SubscriptionResponseDto | null): QuotaViewModel | null => {
  if (!quota) return null;

  const hasPremiumSubscription = isPremiumSubscription(subscription);
  const baseLimit = hasPremiumSubscription ? premiumEntitlementLimit(subscription) : FREE_TIER_TOKEN_LIMIT;
  const limit = quota.limit && quota.limit > 0 ? quota.limit : baseLimit;
  const used = Math.max(0, normalizeNumber(quota.used));
  const remaining = Math.max(0, normalizeNumber(quota.remaining, limit - used));
  const ratio = limit > 0 ? used / limit : 0;
  const percentUsed = Math.min(100, Math.round(ratio * 1000) / 10);

  let status: QuotaUsageStatus = "normal";
  if (quota.isExceeded || ratio >= 1) {
    status = "exceeded";
  } else if (ratio >= QUOTA_CRITICAL_THRESHOLD) {
    status = "critical";
  } else if (ratio >= QUOTA_WARNING_THRESHOLD) {
    status = "warning";
  }

  return {
    used,
    limit,
    remaining,
    resetDate: quota.resetDate,
    percentUsed,
    tier: hasPremiumSubscription ? "premium" : "free",
    tierLabel: hasPremiumSubscription ? "Premium" : "Free Tier",
    status,
  };
};

const extractQuotaInfo = (payload: unknown): QuotaInfo | null => {
  if (!payload || typeof payload !== "object") return null;
  const candidate = payload as Partial<QuotaExceededResponse>;
  if (!candidate.quotaInfo) return null;
  const { used, limit, remaining, resetDate } = candidate.quotaInfo;
  if (typeof used !== "number" || typeof limit !== "number" || typeof remaining !== "number") {
    return null;
  }
  return {
    used,
    limit,
    remaining,
    resetDate: typeof resetDate === "string" ? resetDate : undefined,
  };
};

const quotaToInfo = (quota: QuotaStatusDto): QuotaInfo => ({
  used: quota.used,
  limit: quota.limit,
  remaining: quota.remaining,
  resetDate: quota.resetDate,
});

export const PaymentStore = signalStore(
  { providedIn: "root" },
  withState<PaymentState>({
    quota: null,
    subscription: null,
    quotaView: null,
    initialized: false,
    quotaHydrated: false,
    subscriptionHydrated: false,
    quotaLoadStatus: "loading",
    subscriptionLoadStatus: "loading",
    premiumQuotaPending: false,
    isPollingSubscription: false,
    checkoutInFlight: false,
    portalInFlight: false,
    lastNotifiedStatus: null,
    serviceUnavailable: false,
  }),
  withLoading(),
  withProps(() => ({
    paymentService: inject(PaymentService),
    snackBar: inject(MatSnackBar),
    dialog: inject(MatDialog),
  })),
  withMethods(store => {
    let subscriptionDialogRef: MatDialogRef<SubscriptionRequiredDialogComponent, boolean> | null = null;
    let subscriptionPollingSub: Subscription | null = null;
    let premiumQuotaPollingSub: Subscription | null = null;
    let portalVisitPollingSub: Subscription | null = null;
    let refreshQuotaAfterPremiumActivation: () => void = () => undefined;

    const stopSubscriptionPolling = (): void => {
      subscriptionPollingSub?.unsubscribe();
      subscriptionPollingSub = null;
      patchState(store, { isPollingSubscription: false });
    };

    const showUpgradeDialog = (reason: PaymentDialogReason = "subscription"): void => {
      if (subscriptionDialogRef || isPremiumSubscription(store.subscription())) {
        return;
      }

      const data: SubscriptionRequiredDialogData = {
        hasActiveSubscription: store.subscription()?.hasActiveSubscription ?? false,
        reason,
      };

      subscriptionDialogRef = store.dialog.open(SubscriptionRequiredDialogComponent, {
        data,
        autoFocus: false,
        restoreFocus: true,
        panelClass: ["co-dialog-panel"],
      });

      const closeSub = subscriptionDialogRef.afterClosed().subscribe(() => {
        subscriptionDialogRef = null;
        closeSub.unsubscribe();
      });
    };

    const handleThresholdChange = (view: QuotaViewModel | null): void => {
      if (!store.quotaHydrated() || !store.subscriptionHydrated() || !store.quota() || !store.subscription()) {
        return;
      }

      const currentStatus = view?.status ?? null;
      const previousStatus = store.lastNotifiedStatus();

      if (currentStatus === previousStatus) {
        return;
      }

      patchState(store, { lastNotifiedStatus: currentStatus });

      if (!view) {
        return;
      }

      if (store.premiumQuotaPending()) {
        store.snackBar.open(
          "Premium is active, but your upgraded quota is still being applied. Current quota limits remain in effect for now.",
          undefined,
          { duration: SNACKBAR_DURATION },
        );
        return;
      }

      if (view.status === "warning") {
        const ref = store.snackBar.open("You're approaching your monthly token quota.", "Upgrade", {
          duration: SNACKBAR_DURATION,
        });
        const action$ = ref.onAction();
        const upgradeSub = action$.subscribe(() => {
          showUpgradeDialog("softPrompt");
          upgradeSub.unsubscribe();
        });
      } else if (view.status === "critical") {
        const ref = store.snackBar.open("You have less than 5% of your monthly tokens remaining.", "Upgrade", {
          duration: SNACKBAR_DURATION,
        });
        const action$ = ref.onAction();
        const upgradeSub = action$.subscribe(() => {
          showUpgradeDialog("softPrompt");
          upgradeSub.unsubscribe();
        });
      } else if (view.status === "exceeded") {
        showUpgradeDialog("quota");
      }
    };

    const handleQuotaUpdate = (quota: QuotaStatusDto | null): void => {
      const subscription = store.subscription();
      const nextView = mapQuotaView(quota, subscription);
      patchState(store, {
        quota,
        quotaView: nextView,
        quotaHydrated: true,
        quotaLoadStatus: "ready",
        premiumQuotaPending: isPremiumQuotaPending(quota, subscription),
        serviceUnavailable: false,
      });
      handleThresholdChange(nextView);
    };

    const handleSubscriptionUpdate = (subscription: SubscriptionResponseDto | null): void => {
      const wasPremium = isPremiumSubscription(store.subscription());
      const isPremium = isPremiumSubscription(subscription);
      const quota = store.quota();
      const nextView = mapQuotaView(quota, subscription);
      patchState(store, {
        subscription,
        quotaView: nextView,
        subscriptionHydrated: true,
        subscriptionLoadStatus: "ready",
        premiumQuotaPending: isPremiumQuotaPending(quota, subscription),
      });
      handleThresholdChange(nextView);
      if (isPremium) {
        clearPendingCheckout();
        subscriptionDialogRef?.close(false);
        if (!wasPremium) {
          refreshQuotaAfterPremiumActivation();
        }
      }
    };

    const extractErrorMessage = (error: HttpErrorResponse): string | undefined => {
      if (typeof error.error === "string") {
        return error.error;
      }
      if (error.error && typeof error.error === "object" && "message" in error.error) {
        const messageValue = (error.error as { message?: unknown }).message;
        if (typeof messageValue === "string") {
          return messageValue;
        }
      }
      return undefined;
    };

    const handleRestrictedError = (error: unknown, _context?: string): boolean => {
      if (!(error instanceof HttpErrorResponse)) {
        console.error("Unexpected payment error", error);
        return false;
      }

      if (error.status === 402) {
        showUpgradeDialog("subscription");
        return true;
      }

      if (error.status === 429) {
        const info = extractQuotaInfo(error.error) ?? quotaToInfo(store.quota() ?? fallbackQuota());
        handleQuotaUpdate({ ...info, isExceeded: true });
        if (store.premiumQuotaPending()) {
          store.snackBar.open(
            "This request is still subject to your current quota while the Premium allowance is being activated.",
            undefined,
            { duration: SNACKBAR_DURATION },
          );
        } else {
          showUpgradeDialog("quota");
        }
        return true;
      }

      if (error.status === 503) {
        patchState(store, { serviceUnavailable: true });
        store.snackBar.open("Payment service is temporarily unavailable. Try again shortly.", undefined, {
          duration: SNACKBAR_DURATION,
        });
        return true;
      }

      return false;
    };

    const handleQuotaRequestError = (error: unknown): void => {
      // Billing hydrates in the background on first load. Announcing that failure interrupted work
      // the customer was already doing — and because quota and subscription load together, they got
      // the same toast twice. The unavailable state is recorded either way and shown where billing
      // actually matters, so only a refresh the customer asked for speaks up.
      const announce = store.quotaHydrated();
      patchState(store, { quotaHydrated: true, quotaLoadStatus: "unavailable", serviceUnavailable: true });
      if (error instanceof HttpErrorResponse) {
        if (error.status === 404 || error.status === 503) {
          if (announce) {
            store.snackBar.open("Billing information is temporarily unavailable. Please try again shortly.", undefined, {
              duration: SNACKBAR_DURATION,
            });
          }
          return;
        }
        if (handleRestrictedError(error)) {
          return;
        }
      }
      console.error("Failed to load quota information", error);
    };

    const handleSubscriptionRequestError = (error: unknown): void => {
      // Same reasoning as the quota handler above: the first, unprompted load stays quiet.
      const announce = store.subscriptionHydrated();
      patchState(store, { subscriptionHydrated: true, subscriptionLoadStatus: "unavailable", serviceUnavailable: true });
      if (error instanceof HttpErrorResponse) {
        if (error.status === 404 || error.status === 503) {
          if (announce) {
            store.snackBar.open("Billing information is temporarily unavailable. Please try again shortly.", undefined, {
              duration: SNACKBAR_DURATION,
            });
          }
          return;
        }
        if (handleRestrictedError(error)) {
          return;
        }
      }
      console.error("Failed to load subscription information", error);
    };

    const startSubscriptionPolling = (): void => {
      if (subscriptionPollingSub) return;

      patchState(store, { isPollingSubscription: true });
      let pollCount = 0;
      const maxPolls = 15;

      subscriptionPollingSub = timer(0, 4000)
        .pipe(
          switchMap(() => {
            pollCount++;
            return store.paymentService.paymentControllerGetSubscriptionV1().pipe(
              tap({
                next: (subscription: SubscriptionResponseDto) => {
                  handleSubscriptionUpdate(subscription);
                  if (isPremiumSubscription(subscription)) {
                    store.snackBar.open("Subscription activated! Premium quota unlocked.", undefined, {
                      duration: SNACKBAR_DURATION,
                    });
                    stopSubscriptionPolling();
                  } else if (pollCount >= maxPolls) {
                    clearPendingCheckout();
                    stopSubscriptionPolling();
                    store.snackBar.open(
                      "Subscription activation is taking longer than expected. Refresh your plan status shortly.",
                      undefined,
                      {
                        duration: SNACKBAR_DURATION,
                      },
                    );
                  }
                },
                error: (error: unknown) => {
                  if (pollCount >= maxPolls) {
                    clearPendingCheckout();
                    handleSubscriptionRequestError(error);
                    stopSubscriptionPolling();
                  } else {
                    console.error("Subscription activation check failed; retrying", error);
                  }
                },
              }),
              catchError(() => EMPTY),
            );
          }),
        )
        .subscribe();
    };

    const loadQuota = rxMethod<void>(
      pipe(
        tap(() => store.setLoading(true)),
        switchMap(() =>
          store.paymentService.paymentControllerGetQuotaV1().pipe(
            tapResponse({
              next: (quota: QuotaStatusDto) => handleQuotaUpdate(quota),
              error: (error: unknown) => {
                handleQuotaRequestError(error);
                return EMPTY;
              },
              finalize: () => store.setLoading(false),
            }),
            catchError((error: unknown) => {
              handleQuotaRequestError(error);
              return EMPTY;
            }),
          ),
        ),
      ),
    );

    const stopPremiumQuotaPolling = (): void => {
      premiumQuotaPollingSub?.unsubscribe();
      premiumQuotaPollingSub = null;
    };

    const startPremiumQuotaPolling = (): void => {
      if (premiumQuotaPollingSub) return;

      let pollCount = 0;
      const maxPolls = 5;

      premiumQuotaPollingSub = timer(0, 2000)
        .pipe(
          switchMap(() => {
            pollCount++;
            return store.paymentService.paymentControllerGetQuotaV1().pipe(
              tap({
                next: (quota: QuotaStatusDto) => {
                  handleQuotaUpdate(quota);
                  const subscription = store.subscription();
                  const entitlementLimit = premiumEntitlementLimit(subscription);
                  const quotaReflectsPremium = quota.limit >= entitlementLimit && !quota.isExceeded;

                  if (quotaReflectsPremium || pollCount >= maxPolls) {
                    stopPremiumQuotaPolling();
                  }
                },
                error: (error: unknown) => {
                  if (pollCount >= maxPolls) {
                    handleQuotaRequestError(error);
                    stopPremiumQuotaPolling();
                  } else {
                    console.error("Premium quota refresh failed; retrying", error);
                  }
                },
              }),
              catchError(() => EMPTY),
            );
          }),
        )
        .subscribe();
    };
    refreshQuotaAfterPremiumActivation = startPremiumQuotaPolling;

    const stopPortalVisitPolling = (): void => {
      portalVisitPollingSub?.unsubscribe();
      portalVisitPollingSub = null;
    };

    const startPortalVisitPolling = (): void => {
      if (portalVisitPollingSub) return;

      let pollCount = 0;
      const maxPolls = 5;

      portalVisitPollingSub = timer(0, 2000)
        .pipe(
          switchMap(() => {
            pollCount++;
            return store.paymentService.paymentControllerGetSubscriptionV1().pipe(
              tap({
                next: (subscription: SubscriptionResponseDto) => {
                  handleSubscriptionUpdate(subscription);
                  loadQuota();

                  if (pollCount >= maxPolls) {
                    stopPortalVisitPolling();
                  }
                },
                error: (error: unknown) => {
                  console.error("Error during portal visit polling:", error);
                  handleSubscriptionRequestError(error);

                  if (pollCount >= maxPolls) {
                    stopPortalVisitPolling();
                  }
                },
              }),
              catchError(() => EMPTY),
            );
          }),
        )
        .subscribe();
    };

    const loadSubscription = rxMethod<void>(
      pipe(
        tap(() => store.setLoading(true)),
        switchMap(() =>
          store.paymentService.paymentControllerGetSubscriptionV1().pipe(
            tapResponse({
              next: (subscription: SubscriptionResponseDto) => {
                handleSubscriptionUpdate(subscription);
              },
              error: (error: unknown) => {
                console.error("Failed to load subscription:", error);
                handleSubscriptionRequestError(error);
                return EMPTY;
              },
              finalize: () => store.setLoading(false),
            }),
          ),
        ),
      ),
    );

    return {
      initialize(): void {
        if (store.initialized()) {
          return;
        }

        patchState(store, { initialized: true });
        loadQuota();
        loadSubscription();

        if (hasPendingCheckout()) {
          startSubscriptionPolling();
        }
      },
      pollSubscriptionStatus(): void {
        startSubscriptionPolling();
      },
      loadQuota,
      loadSubscription,
      ensureCanSubmitQuestion(): boolean {
        if (!store.quotaHydrated() || !store.subscriptionHydrated()) return true;
        const view = store.quotaView();
        if (!view) return true;
        if (view.status === "exceeded") {
          showUpgradeDialog("quota");
          return false;
        }
        if (!store.subscription()?.hasActiveSubscription && view.status !== "normal" && hasPendingCheckout()) {
          startSubscriptionPolling();
        }
        return true;
      },
      handleRestrictedError,
      showUpgradeDialog,
      async startCheckout(redirectUrl?: string): Promise<void> {
        if (store.checkoutInFlight()) return;

        patchState(store, { checkoutInFlight: true });

        const dto: GenerateCheckoutDto = {};
        if (redirectUrl) {
          dto.redirectUrl = redirectUrl;
        }

        try {
          const response: InlineResponse2001 = await firstValueFrom(
            store.paymentService.paymentControllerGenerateCheckoutUrlV1({ generateCheckoutDto: dto }),
          );
          if (response?.checkoutUrl) {
            markPendingCheckout();
            window.location.assign(response.checkoutUrl);
          } else {
            store.snackBar.open("Unable to start checkout. Please try again.", undefined, { duration: SNACKBAR_DURATION });
          }
        } catch (error) {
          if (error instanceof HttpErrorResponse && error.status === 400) {
            const message = extractErrorMessage(error) ?? "You already have an active subscription.";

            // Check if the error is about a cancelled subscription
            const isCancelledSubscriptionError =
              message.toLowerCase().includes("cancelled") || message.toLowerCase().includes("manage existing subscription");

            if (isCancelledSubscriptionError) {
              patchState(store, { checkoutInFlight: false, portalInFlight: true });

              const portalDto: GeneratePortalDto = {};
              if (redirectUrl) {
                portalDto.redirectUrl = redirectUrl;
              }

              try {
                const portalResponse: InlineResponse2002 = await firstValueFrom(
                  store.paymentService.paymentControllerGeneratePortalUrlV1({ generatePortalDto: portalDto }),
                );
                if (portalResponse?.portalUrl) {
                  markPendingPortalVisit();
                  window.location.assign(portalResponse.portalUrl);
                } else {
                  store.snackBar.open("Unable to open subscription portal. Please try again.", undefined, {
                    duration: SNACKBAR_DURATION,
                  });
                  patchState(store, { portalInFlight: false });
                }
              } catch (portalError) {
                if (!handleRestrictedError(portalError)) {
                  console.error("Failed to open subscription portal", portalError);
                  store.snackBar.open("Unable to open subscription portal. Please try again later.", undefined, {
                    duration: SNACKBAR_DURATION,
                  });
                }
                patchState(store, { portalInFlight: false });
              }
              return;
            } else {
              store.snackBar.open(message, undefined, {
                duration: SNACKBAR_DURATION,
              });
            }
          } else if (!handleRestrictedError(error)) {
            console.error("Failed to start checkout", error);
            store.snackBar.open("Unable to start checkout. Please try again later.", undefined, {
              duration: SNACKBAR_DURATION,
            });
          }
        } finally {
          patchState(store, { checkoutInFlight: false });
        }
      },
      async openPortal(redirectUrl?: string): Promise<void> {
        if (store.portalInFlight()) return;

        patchState(store, { portalInFlight: true });

        const dto: GeneratePortalDto = {};
        if (redirectUrl) {
          dto.redirectUrl = redirectUrl;
        }

        try {
          const response: InlineResponse2002 = await firstValueFrom(
            store.paymentService.paymentControllerGeneratePortalUrlV1({ generatePortalDto: dto }),
          );
          if (response?.portalUrl) {
            markPendingPortalVisit();
            window.location.assign(response.portalUrl);
          } else {
            store.snackBar.open("Unable to open subscription portal. Please try again.", undefined, {
              duration: SNACKBAR_DURATION,
            });
          }
        } catch (error) {
          if (error instanceof HttpErrorResponse && (error.status === 400 || error.status === 404)) {
            console.error("Failed to open subscription portal", extractErrorMessage(error) ?? error);
            store.snackBar.open("You don't have a billing account yet. Subscribe to set one up.", undefined, {
              duration: SNACKBAR_DURATION,
            });
          } else if (!handleRestrictedError(error)) {
            console.error("Failed to open subscription portal", error);
            store.snackBar.open("Unable to open subscription portal. Please try again later.", undefined, {
              duration: SNACKBAR_DURATION,
            });
          }
        } finally {
          patchState(store, { portalInFlight: false });
        }
      },
      refreshSubscriptionData(): void {
        const returnedFromPortal = hasPendingPortalVisit();
        if (returnedFromPortal) {
          clearPendingPortalVisit();
        }

        loadSubscription();
        loadQuota();

        if (returnedFromPortal) {
          // Poll after a portal visit to catch payment-provider eventual consistency.
          startPortalVisitPolling();
        }
      },
      markPendingPortalVisit,
      hasPendingPortalVisit,
      handleQuotaRequestError,
      handleSubscriptionRequestError,
    };
  }),
  withDevtools(StorageKeys.PaymentStore),
);
